import { EVENTS_STATUS } from "../useSSERequest";

import type { ContentEventType } from "./types";
import type { ToolCallLog } from "@/contexts/ChatContextTypes";

export const createContentEventHandler = (
  setStreamingAnswer: React.Dispatch<React.SetStateAction<string>>,
  setToolCallLogs: React.Dispatch<React.SetStateAction<ToolCallLog[]>>,
  setCitationGenerationStarted: React.Dispatch<React.SetStateAction<boolean>>,
  setAiMessageId?: React.Dispatch<React.SetStateAction<string | null>>
) => {
  return (eventData: ContentEventType) => {
    const timestamp = Date.now();

    // Safety check for eventData
    if (!eventData || typeof eventData !== "object") {
      console.warn("Invalid event data received:", eventData);
      return;
    }

    switch (eventData.status) {
      case EVENTS_STATUS.MESSAGE_ID: {
        const requestId = eventData.data?.request_id as string | undefined;
        if (requestId) {
          setAiMessageId?.(requestId);
        }
        break;
      }

      case EVENTS_STATUS.TEXT_DELTA: {
        setStreamingAnswer(
          (prevState) => prevState + (eventData.data?.content || "")
        );
        break;
      }

      case EVENTS_STATUS.TOOL_CALL_INITIATED: {
        const tool_id = eventData.data?.tool_id as string | undefined;
        const tool_name = eventData.data?.tool_name as string | undefined;
        const display_name = eventData.data?.display_name as string | undefined;
        const content = eventData.data?.content as string | undefined;

        setToolCallLogs((logs) => [
          ...logs,
          {
            type: "TOOL_INITIATED",
            timestamp,
            tool_id,
            tool_name,
            display_name,
            content,
          },
        ]);

        break;
      }

      case EVENTS_STATUS.TOOL_CALL_ARGUMENTS: {
        const tool_id = eventData.data?.tool_id as string | undefined;
        const structuredArguments =
          eventData.data?.arguments || eventData.data?.data;
        const content = eventData.data?.content as string | undefined;

        setToolCallLogs((logs) => [
          ...logs,
          {
            type: "TOOL_ARGUMENTS",
            timestamp,
            tool_id,
            arguments: structuredArguments,
            content,
          },
        ]);

        break;
      }

      case "TOOL_CALL_ARGUMENTS_RAW": {
        const tool_id = eventData.data?.tool_id as string | undefined;
        const arguments_raw = eventData.data?.arguments_raw as
          | string
          | undefined;
        const content = eventData.data?.content as string | undefined;

        setToolCallLogs((logs) => [
          ...logs,
          {
            type: "TOOL_ARGUMENTS_RAW",
            timestamp,
            tool_id,
            arguments_raw,
            content,
          },
        ]);

        break;
      }

      case EVENTS_STATUS.TOOL_CALL_FINISHED: {
        const output = eventData.data?.output;
        const tool_id = eventData.data?.tool_id as string | undefined;
        const tool_name = eventData.data?.tool_name as string | undefined;
        const error = eventData.data?.error;
        const content = eventData.data?.content as string | undefined;

        setToolCallLogs((logs) => [
          ...logs,
          {
            type: "TOOL_FINISHED",
            timestamp,
            tool_id,
            tool_name,
            output,
            error,
            content,
          },
        ]);

        break;
      }

      case EVENTS_STATUS.REASONING_SUMMARY: {
        const summary = eventData.data?.summary as string | undefined;
        const short_summary = eventData.data?.short_summary as
          | string[]
          | undefined;
        const content = eventData.data?.content as string | undefined;

        setToolCallLogs((logs) => [
          ...logs,
          {
            type: "REASONING",
            timestamp,
            summary,
            short_summary,
            content,
          },
        ]);
        break;
      }

      case EVENTS_STATUS.GENERATING_CITATIONS: {
        setCitationGenerationStarted(true);
        break;
      }

      case EVENTS_STATUS.DONE: {
        setCitationGenerationStarted(false);
        break;
      }

      case EVENTS_STATUS.ERROR: {
        const errorDetail =
          typeof eventData.data?.error === "string"
            ? eventData.data.error
            : typeof eventData.data?.error === "object" &&
                eventData.data.error?.message
              ? (eventData.data.error as { message: string }).message
              : "Something went wrong. Please try again.";

        setCitationGenerationStarted(false);
        setStreamingAnswer((prev) =>
          prev
            ? `${prev}\n\n*Error: ${errorDetail}*`
            : `*Error: ${errorDetail}*`
        );
        break;
      }

      default: {
        break;
      }
    }
  };
};
